import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Alert, Badge, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import {
  byGmvDescending,
  countBy,
  filterRows,
  REVIEWED_FILTERS,
  verdictOf,
  VERDICTS,
} from 'components/bvcm_review/bvcmReview';
import { BVCM_RUN_RESULTS, BVCM_RUN_REVIEWS, getURL } from 'lib/networking/endpoints';
import BvcmResultDetail from 'components/bvcm_review/BvcmResultDetail';
import { backgroundGrey, coolGrey, greyStroke } from 'lib/css/colors';
import EmptyState from 'components/shared/EmptyState';
import { formatRoundedCurrency } from 'lib/utils/FormatUtils';
import FsaLoader from 'components/shared/FsaLoader';
import LinkButton from 'components/shared/LinkButton';
import PaginationComponent from 'components/shared/PaginationComponent';
import useGet from 'lib/hooks/useGet';

const DEFAULT_PAGE_SIZE = 25;

// One run's writes and their verdicts. Mounted per run, so a run starts with none of the last
// one's data or errors.
function BvcmRunReview({ runId, canReview }) {
  const [action, setAction] = useState('');
  const [reviewed, setReviewed] = useState(REVIEWED_FILTERS[0].value);
  const [expanded, setExpanded] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);

  const {
    data: { data: results } = {},
    loading,
    error,
  } = useGet(getURL(BVCM_RUN_RESULTS, { run_id: runId }));
  const rows = useMemo(() => results?.rows || [], [results]);

  // Read apart from the rows, so a save refreshes all verdicts without re-reading the report.
  const {
    data: reviewsResponse,
    loading: reviewsLoading,
    error: reviewsError,
    isLoaded: reviewsLoaded,
    silentRefetch: refetchReviews,
  } = useGet(getURL(BVCM_RUN_REVIEWS, { run_id: runId }));
  const reviews = useMemo(() => reviewsResponse?.data || {}, [reviewsResponse]);
  // Until the verdicts arrive every row would read "Not reviewed", inviting a second review.
  // A failed refresh after a save keeps the verdicts already on screen and only warns.
  const verdictsPending = reviewsLoading || !reviewsLoaded;

  useEffect(() => {
    setExpanded(undefined);
    setCurrentPage(1);
  }, [action, reviewed]);

  const actionCounts = countBy(rows, 'action');
  const rowReviews = rows.map(row => reviews[row.fsa_vendor_product_id]).filter(Boolean);
  const verdictCounts = countBy(rowReviews, 'verdict');
  const reviewedCount = rowReviews.length.toLocaleString('en-US');
  const writeCount = rows.length.toLocaleString('en-US');
  const visible = filterRows(rows, reviews, { action, reviewed }).sort(byGmvDescending);
  const page = visible.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <>
      {(loading || verdictsPending) && <FsaLoader label="Loading results" />}
      {error && (
        <Alert variant="danger">
          {error.response?.data?.message || 'Could not load the results of this run.'}
        </Alert>
      )}
      {reviewsError && !reviewsResponse && (
        <Alert variant="danger">
          Could not load the verdicts given on this run, so it cannot be reviewed right now.
        </Alert>
      )}
      {reviewsError && reviewsResponse && (
        <Alert variant="warning">
          Could not refresh the verdicts after a change. Reload the page to be sure the counts are
          current.
        </Alert>
      )}
      {!loading && !verdictsPending && reviewsResponse && results && (
        <>
          <p className={css(styles.summary)}>
            {`${reviewedCount} of ${writeCount} writes reviewed`}
            {VERDICTS.map(verdict => (
              <Badge key={verdict.key} variant={verdict.variant} className="ml-2">
                {verdict.label} {(verdictCounts[verdict.key] || 0).toLocaleString('en-US')}
              </Badge>
            ))}
          </p>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                as="select"
                aria-label="Action"
                value={action}
                onChange={event => setAction(event.target.value)}
              >
                <option value="">All actions</option>
                {Object.entries(actionCounts).map(([code, count]) => (
                  <option key={code} value={code}>
                    {code} ({count.toLocaleString('en-US')})
                  </option>
                ))}
              </Form.Control>
            </Col>
            <Col md={3}>
              <Form.Control
                as="select"
                aria-label="Reviewed"
                value={reviewed}
                onChange={event => setReviewed(event.target.value)}
              >
                {REVIEWED_FILTERS.map(filter => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          {rows.length === 0 && (
            <EmptyState
              title="No writes to review"
              description="This run plans no writes. Its report holds the rows it left alone."
            />
          )}
          {rows.length > 0 && visible.length === 0 && (
            <EmptyState title="Nothing here" description="No writes match these filters." />
          )}
          {visible.length > 0 && (
            <>
              <Table hover className={css(styles.table)}>
                <thead>
                  <tr>
                    <th>Distributor product</th>
                    <th title="Annualized GMV of the distributor product, refreshed nightly">
                      GMV
                    </th>
                    <th>Action</th>
                    <th>Target</th>
                    <th>Audit</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {page.map(row => {
                    const productId = row.fsa_vendor_product_id;
                    const review = reviews[productId];
                    const verdict = review && verdictOf(review.verdict);
                    const isOpen = expanded === productId;
                    const toggle = () => setExpanded(isOpen ? undefined : productId);
                    return (
                      <Fragment key={productId}>
                        <tr className={css(styles.row)} onClick={toggle}>
                          <td>
                            <LinkButton
                              text={row.distributor_product_name}
                              ariaExpanded={isOpen}
                              onClick={event => {
                                event.stopPropagation();
                                toggle();
                              }}
                            />
                            <div className={css(styles.muted)}>
                              {row.distributor_mfr_product_code} · {row.distributor_brand_name}
                            </div>
                          </td>
                          <td className="text-nowrap">{formatRoundedCurrency(row.gmv) || '-'}</td>
                          <td className="text-nowrap">{row.action}</td>
                          <td>
                            {row.action === 'CREATE' ? 'New product' : row.fsa_matched_product}
                            {row.fsa_matched_product_sku && (
                              <div className={css(styles.muted)}>
                                SKU {row.fsa_matched_product_sku}
                              </div>
                            )}
                          </td>
                          <td>
                            {row.audit_verdict} {row.audit_confidence}
                          </td>
                          <td>
                            {verdict ? (
                              <>
                                <Badge variant={verdict.variant}>{verdict.label}</Badge>
                                <div className={css(styles.muted)}>{review.reviewer_name}</div>
                                {review.review_note && (
                                  <div
                                    className={css(styles.muted, styles.note)}
                                    title={review.review_note}
                                  >
                                    {review.review_note}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className={css(styles.muted)}>Not reviewed</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={6} className={css(styles.detailCell)}>
                              <BvcmResultDetail
                                runId={runId}
                                row={row}
                                review={review}
                                canReview={canReview}
                                onReviewChange={refetchReviews}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
              <PaginationComponent
                totalItems={visible.length}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  summary: {
    color: coolGrey,
    marginTop: 8,
  },
  table: {
    border: `1px solid ${greyStroke}`,
  },
  row: {
    cursor: 'pointer',
  },
  muted: {
    color: coolGrey,
    fontSize: 12,
  },
  note: {
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailCell: {
    padding: 0,
    backgroundColor: backgroundGrey,
  },
});

BvcmRunReview.propTypes = {
  runId: PropTypes.number.isRequired,
  canReview: PropTypes.bool,
};

export default BvcmRunReview;
